import React, { useMemo } from 'react'
import {
  OrderStatusData,
  DiningOptionBehavior
} from 'src/apollo/onlineOrdering'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'
import { t } from 'src/translations/src'
import {
  getUnifiedOrderStatusWithCompletedOrder,
  UnifiedOrderStatus
} from '../order_tracker/order_status_bar/statusUtil'
import Image from 'src/shared/components/common/Image'
import { useRestaurantRoutes } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { ButtonLink } from 'src/shared/components/common/button'
import { getOrderStatusSectionTexts } from './getOrderStatusSectionTexts'

export const OrderStatusSection = ({
  orderStatusInfo,
  orderStatusInfoLoading
}: {
  orderStatusInfo?: OrderStatusData
  orderStatusInfoLoading: boolean
}) => {
  const { completedOrder } = useCompletedOrder()
  const { selectedLocation } = useRestaurant()
  const customerEmail = completedOrder?.customerV3?.email
  const restaurantName = getDisplayBusinessName(
    completedOrder?.restaurant.name,
    selectedLocation?.name
  )
  const isDeliveryOrder =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery

  const numberOfSteps = isDeliveryOrder ? 4 : 3

  const unifiedOrderStatus: UnifiedOrderStatus = useMemo(() => {
    return getUnifiedOrderStatusWithCompletedOrder(
      orderStatusInfo,
      orderStatusInfoLoading,
      completedOrder
    )
  }, [orderStatusInfo, orderStatusInfoLoading, completedOrder])

  const imageSource = useMemo(() => {
    switch (unifiedOrderStatus) {
      case UnifiedOrderStatus.RECEIVED:
      case UnifiedOrderStatus.CONFIRMED:
        return 'icons/orderConfirmation/order-confirmed.svg'
      case UnifiedOrderStatus.SCHEDULED:
        return 'icons/orderConfirmation/order-scheduled.svg'
      case UnifiedOrderStatus.PREPARING:
        return 'icons/orderConfirmation/preparing-order.svg'
      case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT:
      case UnifiedOrderStatus.DRIVER_AT_RESTAURANT:
        return 'icons/orderConfirmation/picking-up-order.svg'
      case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF:
      case UnifiedOrderStatus.DRIVER_AT_DROPOFF:
        return 'icons/orderConfirmation/out-for-delivery.svg'
      case UnifiedOrderStatus.READY_FOR_PICKUP:
      case UnifiedOrderStatus.LIKELY_READY:
        // Show preparing order if delivery
        return isDeliveryOrder
          ? 'icons/orderConfirmation/preparing-order.svg'
          : 'icons/orderConfirmation/ready-for-pickup.svg'
      case UnifiedOrderStatus.COMPLETE:
      default:
        return 'icons/orderConfirmation/bag-with-checkmark.svg'
    }
  }, [unifiedOrderStatus, isDeliveryOrder])

  const { headerText, subtitle } = useMemo(
    () =>
      getOrderStatusSectionTexts({
        unifiedOrderStatus,
        isDeliveryOrder,
        restaurantName,
        customerEmail,
        lastUpdatedIso: orderStatusInfo?.lastUpdated,
        approvalStatus: completedOrder?.approvalStatus
      }),
    [
      unifiedOrderStatus,
      isDeliveryOrder,
      restaurantName,
      customerEmail,
      orderStatusInfo?.lastUpdated,
      completedOrder?.approvalStatus
    ]
  )

  return (
    <>
      <div
        className='orderConfirmationSection'
        data-testid='order-status-section'
        role='group'
        tabIndex={0}
      >
        <Image
          src={imageSource}
          alt={t('order-confirmation.order-status.icon-alt-text')}
          className='orderStatusIcon'
          data-testid='order-status-icon'
        />
        <div className='orderStatusText' role='status'>
          <h1>{headerText}</h1>
          <p>{subtitle}</p>
        </div>
        <OrderStatusStepper
          numberOfSteps={numberOfSteps}
          orderStatus={unifiedOrderStatus}
        />
      </div>
    </>
  )
}

const orderStatusToStep = new Map<UnifiedOrderStatus, number>([
  [UnifiedOrderStatus.UNKNOWN, 0],
  [UnifiedOrderStatus.SCHEDULED, 0],
  [UnifiedOrderStatus.RECEIVED, 1],
  [UnifiedOrderStatus.CONFIRMED, 1],
  [UnifiedOrderStatus.PREPARING, 2],
  [UnifiedOrderStatus.READY_FOR_PICKUP, 3],
  [UnifiedOrderStatus.LIKELY_READY, 3],
  [UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT, 3],
  [UnifiedOrderStatus.DRIVER_AT_RESTAURANT, 3],
  [UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF, 4],
  [UnifiedOrderStatus.DRIVER_AT_DROPOFF, 4],
  [UnifiedOrderStatus.COMPLETE, 5]
])

const OrderStatusStepper = ({
  numberOfSteps,
  orderStatus
}: {
  numberOfSteps: number
  orderStatus: UnifiedOrderStatus
}) => {
  const { orderPath } = useRestaurantRoutes()
  const currentStep = orderStatusToStep.get(orderStatus) ?? 0
  if (currentStep === 0) {
    return null
  }
  if (currentStep > numberOfSteps) {
    return (
      <ButtonLink
        className='orderConfirmationButton'
        type='button'
        data-testid='order-again-button'
        href={orderPath}
      >
        {t('order-confirmation.order-status.order-again')}
      </ButtonLink>
    )
  }
  return (
    <div className='orderStatusStepper'>
      {Array.from({ length: numberOfSteps }).map((_, index) => {
        const isCompleted = index < currentStep
        const isLatest = index === currentStep - 1
        return (
          <div
            className={`orderStatusStep ${isCompleted ? 'completed' : ''} ${
              isLatest ? 'latestStep' : ''
            }`}
            key={index}
          ></div>
        )
      })}
    </div>
  )
}
